import React, { useEffect } from 'react';
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import FontFamily from '@tiptap/extension-font-family';
import Image from '@tiptap/extension-image';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Typography from '@tiptap/extension-typography';
import CharacterCount from '@tiptap/extension-character-count';
import { Extension } from '@tiptap/core';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, AlignLeft, AlignCenter,
  AlignRight, AlignJustify, List, ListOrdered, Link2, Undo2, Redo2, Highlighter,
  Type, Table as TableIcon, Image as ImageIcon, Quote, Code, Minus, ListChecks,
  Indent, Outdent, RemoveFormatting, Subscript as SubIcon, Superscript as SupIcon,
} from 'lucide-react';

/** Custom font-size mark via TextStyle */
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return { types: ['textStyle'] };
  },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: (el) => el.style.fontSize?.replace(/['"]+/g, '') || null,
          renderHTML: (attrs) => (attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {}),
        },
      },
    }];
  },
  addCommands() {
    return {
      setFontSize: (fontSize) => ({ chain }) => chain().setMark('textStyle', { fontSize }).run(),
      unsetFontSize: () => ({ chain }) => chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

function ToolBtn({ active, disabled, onClick, title, children }) {
  return (
    <button
      type="button"
      className={`gdocs-tb-btn${active ? ' is-active' : ''}`}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );
}

/**
 * Google Docs–like rich text editor powered by TipTap.
 */
export default function DocEditor({
  html,
  editable = true,
  onChange,
  onReady,
  variant = 'page', // page | embedded
  showToolbar = true,
}) {
  const editor = useEditor({
    editable,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        codeBlock: { HTMLAttributes: { class: 'gdocs-code-block' } },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'gdocs-link' },
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      FontSize,
      FontFamily,
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({ placeholder: 'Empieza a escribir o pega contenido…' }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Image.configure({ inline: false, allowBase64: true }),
      Subscript,
      Superscript,
      TaskList,
      TaskItem.configure({ nested: true }),
      Typography,
      CharacterCount,
    ],
    content: html || '<p></p>',
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class: variant === 'embedded'
          ? 'gdocs-tiptap gdocs-tiptap--embedded ProseMirror'
          : 'gdrive-doc-page gdocs-tiptap ProseMirror',
        'data-testid': 'drive-doc-editor',
      },
    },
  });

  // Sync external HTML when file loads (once editor ready)
  useEffect(() => {
    if (!editor || html == null) return;
    const current = editor.getHTML();
    // Avoid loop: only set if substantially different
    if (html !== current && (html || '') !== '<p></p>') {
      editor.commands.setContent(html || '<p></p>', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, html === undefined ? '' : (html || '').slice(0, 80) + String((html || '').length)]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!!editable);
      onReady?.(editor);
    }
  }, [editor, editable, onReady]);

  if (!editor) {
    return (
      <div className="gdrive-doc-canvas">
        <div className="gdrive-doc-page animate-pulse opacity-40 min-h-[400px]" />
      </div>
    );
  }

  const setLink = () => {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('URL del enlace', prev || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const addImage = () => {
    const url = window.prompt('URL de la imagen');
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  return (
    <div className={`gdocs-doc-wrap flex flex-col min-h-0 flex-1${variant === 'embedded' ? ' gdocs-doc-wrap--embedded' : ''}`}>
      {editable && showToolbar && (
        <div className="gdocs-toolbar shrink-0">
          <ToolBtn title="Deshacer" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
            <Undo2 className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn title="Rehacer" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
            <Redo2 className="h-4 w-4" />
          </ToolBtn>
          <span className="gdocs-tb-sep" />

          <select
            className="gdocs-tb-select"
            value={
              editor.isActive('heading', { level: 1 }) ? 'h1'
                : editor.isActive('heading', { level: 2 }) ? 'h2'
                  : editor.isActive('heading', { level: 3 }) ? 'h3'
                    : 'p'
            }
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'p') editor.chain().focus().setParagraph().run();
              else editor.chain().focus().toggleHeading({ level: Number(v.slice(1)) }).run();
            }}
          >
            <option value="p">Normal</option>
            <option value="h1">Título 1</option>
            <option value="h2">Título 2</option>
            <option value="h3">Título 3</option>
          </select>

          <select
            className="gdocs-tb-select"
            defaultValue="Arial"
            onChange={(e) => editor.chain().focus().setFontFamily(e.target.value).run()}
          >
            <option value="Arial">Arial</option>
            <option value="Roboto">Roboto</option>
            <option value="Georgia">Georgia</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Courier New">Courier New</option>
            <option value="Comic Sans MS">Comic Sans</option>
          </select>

          <select
            className="gdocs-tb-select"
            defaultValue="11pt"
            onChange={(e) => editor.chain().focus().setFontSize(e.target.value).run()}
          >
            {['8pt', '9pt', '10pt', '11pt', '12pt', '14pt', '16pt', '18pt', '24pt', '32pt', '48pt'].map((s) => (
              <option key={s} value={s}>{s.replace('pt', '')}</option>
            ))}
          </select>

          <span className="gdocs-tb-sep" />

          <ToolBtn title="Negrita" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
            <Bold className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn title="Cursiva" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
            <Italic className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn title="Subrayado" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
            <UnderlineIcon className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn title="Tachado" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>
            <Strikethrough className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn title="Subíndice" active={editor.isActive('subscript')} onClick={() => editor.chain().focus().toggleSubscript().run()}>
            <SubIcon className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn title="Superíndice" active={editor.isActive('superscript')} onClick={() => editor.chain().focus().toggleSuperscript().run()}>
            <SupIcon className="h-4 w-4" />
          </ToolBtn>

          <label className="gdocs-tb-btn" title="Color de texto">
            <Type className="h-4 w-4" />
            <input
              type="color"
              className="sr-only"
              defaultValue="#1f1f1f"
              onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            />
          </label>
          <label className="gdocs-tb-btn" title="Resaltar">
            <Highlighter className="h-4 w-4" />
            <input
              type="color"
              className="sr-only"
              defaultValue="#fff59d"
              onChange={(e) => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()}
            />
          </label>

          <span className="gdocs-tb-sep" />

          <ToolBtn title="Izquierda" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}>
            <AlignLeft className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn title="Centro" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}>
            <AlignCenter className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn title="Derecha" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}>
            <AlignRight className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn title="Justificado" active={editor.isActive({ textAlign: 'justify' })} onClick={() => editor.chain().focus().setTextAlign('justify').run()}>
            <AlignJustify className="h-4 w-4" />
          </ToolBtn>

          <span className="gdocs-tb-sep" />

          <ToolBtn title="Viñetas" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
            <List className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn title="Numeración" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
            <ListOrdered className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn title="Lista de tareas" active={editor.isActive('taskList')} onClick={() => editor.chain().focus().toggleTaskList().run()}>
            <ListChecks className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn title="Cita" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
            <Quote className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn title="Código" active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
            <Code className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn title="Línea horizontal" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
            <Minus className="h-4 w-4" />
          </ToolBtn>

          <span className="gdocs-tb-sep" />

          <ToolBtn title="Enlace" active={editor.isActive('link')} onClick={setLink}>
            <Link2 className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn title="Imagen" onClick={addImage}>
            <ImageIcon className="h-4 w-4" />
          </ToolBtn>
          <ToolBtn title="Tabla" onClick={insertTable}>
            <TableIcon className="h-4 w-4" />
          </ToolBtn>
          {editor.isActive('table') && (
            <>
              <ToolBtn title="Añadir fila" onClick={() => editor.chain().focus().addRowAfter().run()}>+F</ToolBtn>
              <ToolBtn title="Añadir columna" onClick={() => editor.chain().focus().addColumnAfter().run()}>+C</ToolBtn>
              <ToolBtn title="Eliminar tabla" onClick={() => editor.chain().focus().deleteTable().run()}>×T</ToolBtn>
            </>
          )}
          <ToolBtn title="Quitar formato" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>
            <RemoveFormatting className="h-4 w-4" />
          </ToolBtn>
        </div>
      )}

      {editable && editor && (
        <BubbleMenu editor={editor} tippyOptions={{ duration: 100 }} className="gdocs-bubble">
          <ToolBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Negrita">
            <Bold className="h-3.5 w-3.5" />
          </ToolBtn>
          <ToolBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Cursiva">
            <Italic className="h-3.5 w-3.5" />
          </ToolBtn>
          <ToolBtn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Subrayado">
            <UnderlineIcon className="h-3.5 w-3.5" />
          </ToolBtn>
          <ToolBtn active={editor.isActive('link')} onClick={setLink} title="Enlace">
            <Link2 className="h-3.5 w-3.5" />
          </ToolBtn>
        </BubbleMenu>
      )}

      <div className={variant === 'embedded' ? 'gdocs-embedded-canvas flex-1 overflow-auto' : 'gdrive-doc-canvas flex-1 overflow-auto'}>
        <EditorContent editor={editor} />
        {editable && variant === 'page' && (
          <div className="gdocs-charcount">
            {editor.storage.characterCount?.characters?.() ?? 0} caracteres
            {' · '}
            {editor.storage.characterCount?.words?.() ?? 0} palabras
          </div>
        )}
      </div>
    </div>
  );
}

/** Get HTML from a TipTap editor instance ref callback pattern */
export function getDocHtml(editor) {
  return editor?.getHTML?.() || '<p></p>';
}
